/**
 * Identifica le chiavi alle quali sono associati i valori dei token nel localStorage
 * @enum
 */
enum LocalStorageKeys {
    ACCESS_TOKEN = 'accessToken',
    REFRESH_TOKEN = 'refreshToken',
    AUTH_ACCESS_TOKEN = 'authAccessToken',
    AUTH_LINK_TOKEN = 'authLinkToken',
}

/**
 * Gestisce le operazioni sui token di accesso e di refresh.
 * Opera secondo la logica descritta dal pattern Singleton [GoF]
 */
export class TokenService {
    /**
     * Istanza corrente del servizio
     * @type {TokenService}
     */
    static instance: TokenService;

    /**
     * Permette al costruttore di eseguito solo da questa classe o suoi eredi
     * @protected
     */
    protected constructor() {
    }

    /**
     * Secondo il pattern Singleton [GoF], si assicura che nell'ambiente di esecuzione,
     * sia presente una sola istanza della classe.
     * @returns {TokenService}
     */
    static getInstance() {
        if (!TokenService.instance)
            TokenService.instance = new TokenService();
        return TokenService.instance;
    }

    /**
     * Persiste nel localStorage l'accessToken
     * Vedi https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
     * @param {string} accessToken
     */
    setAccessToken(accessToken: string) {
        localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, accessToken);
    }

    /**
     * Persiste nel localStorage l'accessToken
     * Vedi https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
     * @param {string} accessToken
     */
    setAuthAccessToken(accessToken: string) {
        localStorage.setItem(LocalStorageKeys.AUTH_ACCESS_TOKEN, accessToken);
    }

    /**
     * Persiste nel localStorage l'accessToken
     * Vedi https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
     * @param {string} accessToken
     */
    setAuthLinkToken(accessToken: string) {
        localStorage.setItem(LocalStorageKeys.AUTH_LINK_TOKEN, accessToken);
    }

    /**
     * Persiste nel localStorage il refreshToken
     * Vedi https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
     * @param {string} refreshToken
     */
    setRefreshToken(refreshToken: string) {
        localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, refreshToken);
    }

    /**
     * Wrapper che imposta simultaneamente access e refresh token
     * @param {{accessToken: string, refreshToken: string}} params
     */
    setTokens(params: {
        accessToken: string;
        refreshToken: string;
    }) {
        this.setAccessToken(params.accessToken);
        this.setRefreshToken(params.refreshToken);
    }

    /**
     * Restituisce, se presente, l'accessToken corrente
     * @returns {string | null}
     */
    getAccessToken() {
        return localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
    }

    /**
     * Restituisce, se presente, l'accessToken corrente
     * @returns {string | null}
     */
    getAuthAccessToken() {
        return localStorage.getItem(LocalStorageKeys.AUTH_ACCESS_TOKEN);
    }

    /**
     * Restituisce, se presente, l'accessToken corrente
     * @returns {string | null}
     */
    getAuthLinkToken() {
        return localStorage.getItem(LocalStorageKeys.AUTH_LINK_TOKEN);
    }

    /**
     * Resituisce, se presente, il refreshToken corrente
     * @returns {string | null}
     */
    getRefreshToken() {
        return localStorage.getItem(LocalStorageKeys.REFRESH_TOKEN);
    }

    /**
     * Wrapper che restituisce simultaneamente access e refresh token
     * @returns {{accessToken: string | null, refreshToken: string | null}}
     */
    getTokens() {
        return {
            accessToken: this.getAccessToken(),
            refreshToken: this.getRefreshToken()
        };
    }

    /**
     * Elimina access e refresh token, utilizzato per effettuare il logout dell'utente corrente
     */
    clearTokens() {
        localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, '');
        localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, '');
        localStorage.setItem(LocalStorageKeys.AUTH_ACCESS_TOKEN, '');
        localStorage.setItem(LocalStorageKeys.AUTH_LINK_TOKEN, '');
    }
}